import * as React from "react";
import {
  DynamicDrawUsage,
  Group,
  Texture,
  Vector3,
  InstancedMesh,
  Material,
  MeshLambertMaterial,
  Matrix4,
  Quaternion,
  BufferAttribute,
} from "three";
import { extend, useFrame } from "@react-three/fiber";
import { useTexture } from "@react-three/drei";
import { CloudState, CloudsProps } from "../types";
import { CloudStateContext } from "../context/CloudState";

const CLOUD_URL =
  "https://rawcdn.githack.com/pmndrs/drei-assets/9225a9f1fbd449d9411125c2f419b843d0308c9f/cloud.png";

// Module-level preload so the texture is already cached by the time a fresh
// HomePart/ServicesPart mounts during page navigation — prevents the FBO
// cross-fade quad from being hidden by a Suspense fallback (white flash).
useTexture.preload(CLOUD_URL);

const parentMatrix = /*@__PURE__*/ new Matrix4();
const translation = /*@__PURE__*/ new Vector3();
const rotation = /*@__PURE__*/ new Quaternion();
const cpos = /*@__PURE__*/ new Vector3();
const cquat = /*@__PURE__*/ new Quaternion();
const scale = /*@__PURE__*/ new Vector3();
const context = CloudStateContext;

export const Clouds = React.forwardRef<Group, CloudsProps>(
  (
    {
      children,
      material = MeshLambertMaterial,
      texture = CLOUD_URL,
      range,
      limit = 200,
      ...props
    },
    fref,
  ) => {
    const CloudMaterial = React.useMemo(() => {
      return class extends (material as typeof Material) {
        constructor() {
          super();
          this.onBeforeCompile = (shader) => {
            shader.vertexShader =
              `attribute float opacity;
                 varying float vOpacity;
                ` +
              shader.vertexShader.replace(
                "#include <fog_vertex>",
                `#include <fog_vertex>
                   vOpacity = opacity;
                  `,
              );
            shader.fragmentShader =
              `varying float vOpacity;
                ` +
              shader.fragmentShader.replace(
                "#include <opaque_fragment>",
                `#include <opaque_fragment>
                   gl_FragColor = vec4(outgoingLight, diffuseColor.a * vOpacity);
                  `,
              );
          };
        }
      };
    }, [material]);

    extend({ CloudMaterial });

    const instance = React.useRef<InstancedMesh>(null!);
    const clouds = React.useRef<CloudState[]>([]);
    const opacities = React.useMemo(
      () => new Float32Array(Array.from({ length: limit }, () => 1)),
      [limit],
    );
    const colors = React.useMemo(
      () =>
        new Float32Array(Array.from({ length: limit }, () => [1, 1, 1]).flat()),
      [limit],
    );

    const cloudTexture = useTexture(texture) as Texture;

    let t = 0;
    let index = 0;
    let config: CloudState;
    const qat = new Quaternion();
    const dir = new Vector3(0, 0, 1);
    const pos = new Vector3();

    useFrame((state, delta) => {
      t = state.clock.getElapsedTime();
      parentMatrix.copy(instance.current.matrixWorld).invert();
      state.camera.matrixWorld.decompose(cpos, cquat, scale);

      for (index = 0; index < clouds.current.length; index++) {
        config = clouds.current[index];
        config.ref.current.matrixWorld.decompose(translation, rotation, scale);
        translation.add(
          pos.copy(config.position).applyQuaternion(rotation).multiply(scale),
        );
        rotation
          .copy(cquat)
          .multiply(
            qat.setFromAxisAngle(
              dir,
              (config.rotation += delta * config.rotationFactor),
            ),
          );
        scale.multiplyScalar(
          config.volume +
            ((1 + Math.sin(t * config.density * config.speed)) / 2) *
              config.growth,
        );
        config.matrix
          .compose(translation, rotation, scale)
          .premultiply(parentMatrix);
        config.dist = translation.distanceTo(cpos);
      }

      // Depth-sort. Instances have no specific draw order, w/o sorting z would be random
      clouds.current.sort((a, b) => b.dist - a.dist);
      for (index = 0; index < clouds.current.length; index++) {
        config = clouds.current[index];
        opacities[index] =
          config.opacity *
          (config.dist < config.fade - 1 ? config.dist / config.fade : 1);
        instance.current.setMatrixAt(index, config.matrix);
        instance.current.setColorAt(index, config.color);
      }

      // Update instance
      instance.current.geometry.attributes.opacity.needsUpdate = true;
      instance.current.instanceMatrix.needsUpdate = true;
      if (instance.current.instanceColor)
        instance.current.instanceColor.needsUpdate = true;
    });

    React.useLayoutEffect(() => {
      const updateRange = Math.min(
        limit,
        range !== undefined ? range : limit,
        clouds.current.length,
      );
      instance.current.count = updateRange;
      instance.current.instanceMatrix.updateRange.count = updateRange * 16;
      if (instance.current.instanceColor)
        instance.current.instanceColor.updateRange.count = updateRange * 3;
      (
        instance.current.geometry.attributes.opacity as BufferAttribute
      ).updateRange.count = updateRange;
    });

    let imageBounds = [
      cloudTexture!.image.width ?? 1,
      cloudTexture!.image.height ?? 1,
    ];
    let max = Math.max(imageBounds[0], imageBounds[1]);
    imageBounds = [imageBounds[0] / max, imageBounds[1] / max];

    return (
      <group ref={fref} {...props}>
        <context.Provider value={clouds}>
          {children}
          <instancedMesh
            matrixAutoUpdate={false}
            ref={instance}
            args={[null as any, null as any, limit]}
          >
            <instancedBufferAttribute
              usage={DynamicDrawUsage}
              attach="instanceColor"
              args={[colors, 3]}
            />
            <planeGeometry args={[...imageBounds] as any}>
              <instancedBufferAttribute
                usage={DynamicDrawUsage}
                attach="attributes-opacity"
                args={[opacities, 1]}
              />
            </planeGeometry>
            <cloudMaterial
              key={material.name}
              map={cloudTexture}
              transparent
              depthWrite={false}
            />
          </instancedMesh>
        </context.Provider>
      </group>
    );
  },
);
